import React from 'react';

export default function LoanApplicationForm({
  state,
  actions,
}) {
  const isReadOnly = state.isView;

  return (
    <form onSubmit={actions.handleSaveAndExit} className="space-y-6">
      <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs overflow-hidden max-w-4xl">
        {/* Card Header */}
        <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-primary text-xl">
              {state.isView ? 'visibility' : 'payments'}
            </span>
            <h2 className="font-display text-base font-bold text-black">
              {state.isView
                ? 'View Loan Application'
                : state.isEdit
                ? 'Edit Loan Application'
                : 'Loan Application'}
            </h2>
          </div>
          <span className="text-xs font-semibold text-slate-500 font-mono">
            {state.editId ? `ID: #${state.editId}` : 'New Request'}
          </span>
        </div>

        {/* Form Inputs matching Screenshot 2 */}
        <div className="p-6 space-y-5">
          {/* Apply Date (Read-only) */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Apply Date
            </label>
            <div className="sm:col-span-3">
              <input
                type="text"
                value={state.applyDate}
                readOnly
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/60 px-3.5 py-2.5 text-xs font-bold text-black cursor-not-allowed outline-hidden"
              />
            </div>
          </div>

          {/* Pay Head */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Pay Head {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <select
                value={state.payHead}
                onChange={(e) => actions.setPayHead(e.target.value)}
                disabled={isReadOnly}
                required={!isReadOnly}
                className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-bold text-black outline-hidden transition ${
                  isReadOnly
                    ? 'bg-surface-container-low/60 cursor-not-allowed'
                    : 'bg-surface-container-lowest focus:border-primary cursor-pointer'
                }`}
              >
                {state.payHeadOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Employee (Read-only) */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Employee
            </label>
            <div className="sm:col-span-3">
              <input
                type="text"
                value={state.employee}
                readOnly
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/60 px-3.5 py-2.5 text-xs font-bold text-black cursor-not-allowed outline-hidden"
              />
            </div>
          </div>

          {/* Loan Date */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Loan Date {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <input
                type="date"
                value={state.loanDate}
                onChange={(e) => actions.setLoanDate(e.target.value)}
                disabled={isReadOnly}
                readOnly={isReadOnly}
                required={!isReadOnly}
                className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-semibold text-black outline-hidden transition ${
                  isReadOnly
                    ? 'bg-surface-container-low/60 cursor-not-allowed'
                    : 'bg-surface-container-lowest focus:border-primary focus:ring-1 focus:ring-primary'
                }`}
              />
            </div>
          </div>

          {/* Loan Type */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Loan Type {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <select
                value={state.loanType}
                onChange={(e) => actions.setLoanType(e.target.value)}
                disabled={isReadOnly}
                required={!isReadOnly}
                className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-bold text-black outline-hidden transition ${
                  isReadOnly
                    ? 'bg-surface-container-low/60 cursor-not-allowed'
                    : 'bg-surface-container-lowest focus:border-primary cursor-pointer'
                }`}
              >
                {state.loanTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Loan Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              Loan Amount {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <div className="relative rounded-xl border border-outline-variant/30 overflow-hidden focus-within:border-primary">
                <span className="absolute left-3.5 top-2.5 text-xs font-bold text-slate-500">₹</span>
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={state.loanAmount}
                  onChange={(e) => actions.setLoanAmount(e.target.value)}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  required={!isReadOnly}
                  className={`w-full bg-transparent pl-8 pr-3.5 py-2.5 text-xs font-bold text-black font-sans tabular-nums outline-hidden ${
                    isReadOnly ? 'bg-surface-container-low/60 cursor-not-allowed' : 'bg-surface-container-lowest'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* EMI FromDate */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              EMI FromDate {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <input
                type="date"
                value={state.emiFromDate}
                onChange={(e) => actions.setEmiFromDate(e.target.value)}
                disabled={isReadOnly}
                readOnly={isReadOnly}
                required={!isReadOnly}
                className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-semibold text-black outline-hidden transition ${
                  isReadOnly
                    ? 'bg-surface-container-low/60 cursor-not-allowed'
                    : 'bg-surface-container-lowest focus:border-primary focus:ring-1 focus:ring-primary'
                }`}
              />
            </div>
          </div>

          {/* EMI Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right">
              EMI Amount {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <div className="relative rounded-xl border border-outline-variant/30 overflow-hidden focus-within:border-primary">
                <span className="absolute left-3.5 top-2.5 text-xs font-bold text-slate-500">₹</span>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={state.emiAmount}
                  onChange={(e) => actions.setEmiAmount(e.target.value)}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  required={!isReadOnly}
                  className={`w-full bg-transparent pl-8 pr-3.5 py-2.5 text-xs font-bold text-black font-sans tabular-nums outline-hidden ${
                    isReadOnly ? 'bg-surface-container-low/60 cursor-not-allowed' : 'bg-surface-container-lowest'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="grid grid-cols-1 sm:grid-cols-4 items-start gap-2 sm:gap-4">
            <label className="text-xs font-bold text-slate-700 sm:text-right pt-2.5">
              Remarks {!isReadOnly && <span className="text-rose-500">*</span>}
            </label>
            <div className="sm:col-span-3">
              <textarea
                rows={4}
                value={state.remarks}
                onChange={(e) => actions.setRemarks(e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                placeholder="Enter loan request purpose and repayment details..."
                required={!isReadOnly}
                className={`w-full rounded-xl border border-outline-variant/30 p-3.5 text-xs text-black font-medium outline-hidden transition ${
                  isReadOnly
                    ? 'bg-surface-container-low/60 cursor-not-allowed'
                    : 'bg-surface-container-lowest focus:border-primary'
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Sticky Actions Toolbar */}
      <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 bg-surface-container-lowest p-4 rounded-2xl shadow-xs max-w-4xl">
        {isReadOnly ? (
          /* View Mode: Only Cancel button matching screenshot */
          <button
            type="button"
            onClick={actions.handleCancel}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-5 py-2 text-xs font-bold text-slate-800 hover:bg-surface-container-high transition cursor-pointer shadow-xs"
          >
            Cancel
          </button>
        ) : (
          /* Edit / New Mode */
          <>
            <button
              type="button"
              onClick={actions.handleCancel}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-slate-700 hover:bg-surface-container-high transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={actions.handleSaveAndContinue}
              disabled={state.isSaving}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-high px-4 py-2 text-xs font-bold text-black hover:bg-surface-container-highest transition cursor-pointer"
            >
              {state.isSaving ? 'Saving...' : 'Save & Continue'}
            </button>
            <button
              type="submit"
              disabled={state.isSaving}
              className="rounded-xl bg-primary px-5 py-2 text-xs font-bold text-white shadow-xs hover:bg-primary/90 transition cursor-pointer"
            >
              {state.isSaving ? 'Saving...' : 'Save & Exit'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
